/**
 * GLM実行中の進捗とConvergence Historyを表示するモーダルダイアログ
 */

import { useState } from 'react';
import type { GLMProgress } from '../../../utils/glm';
import { Dialog } from '../../common/Dialog';
import { Button } from '../../common/Button';
import * as styles from './GLMProgressDialog.module.css';

interface GLMProgressDialogProps {
  isOpen: boolean;
  progress: GLMProgress | null;
  progressHistory: GLMProgress[];
  isComplete: boolean;
  onClose: () => void;
  onCancel?: () => void;
  /** 係数の変数名（インターセプト含む） */
  variableNames?: string[];
  /** 収束履歴をデータセットとして保存 */
  onSaveAsDataset?: () => void;
}

export function GLMProgressDialog({ isOpen, progress, progressHistory, isComplete, onClose, onCancel, variableNames, onSaveAsDataset }: GLMProgressDialogProps) {
  const [showAllIterations, setShowAllIterations] = useState<boolean>(false);
  const [datasetSaved, setDatasetSaved] = useState<boolean>(false);

  const hasThetaEstimation = progressHistory.some(p => p.isDuringThetaEstimation);

  // 表示するエントリをフィルタリング
  let displayEntries: GLMProgress[] = [];

  // θ推定中のエントリ（採用されたθのみ）
  const thetaEstimationEntries = progressHistory.filter(p => p.isDuringThetaEstimation && p.isAdoptedTheta);
  // 最後のθイテレーション番号を計算（finalの代わりに使用）
  const maxThetaIteration = hasThetaEstimation && thetaEstimationEntries.length > 0
    ? Math.max(...thetaEstimationEntries.map(e => e.thetaIteration ?? 0))
    : 0;
  const finalThetaIterationNumber = maxThetaIteration + 1;

  if (hasThetaEstimation) {
    // 最終IRLS（θ確定後、thetaIterationがundefinedのもの）
    const finalIRLSEntries = progressHistory.filter(p => !p.isDuringThetaEstimation && p.thetaIteration === undefined);

    if (showAllIterations) {
      displayEntries = [...thetaEstimationEntries, ...finalIRLSEntries];
    } else {
      // 各θでの最終イテレーションのみ
      const thetaGroups: { [key: string]: GLMProgress[] } = {};
      thetaEstimationEntries.forEach(p => {
        const key = `${p.thetaIteration}-${p.theta?.toFixed(6)}`;
        if (!thetaGroups[key]) thetaGroups[key] = [];
        thetaGroups[key].push(p);
      });
      const thetaFinalIters = Object.values(thetaGroups).map(g => g[g.length - 1]);
      displayEntries = [...thetaFinalIters, ...finalIRLSEntries];
    }

    displayEntries.sort((a, b) => {
      const aTheta = a.thetaIteration ?? 999;
      const bTheta = b.thetaIteration ?? 999;
      if (aTheta !== bTheta) return aTheta - bTheta;
      const aT = a.theta ?? 0;
      const bT = b.theta ?? 0;
      if (Math.abs(aT - bT) > 0.0001) return aT - bT;
      return a.iteration - b.iteration;
    });
  } else {
    displayEntries = progressHistory.filter(p => p.stage === 'irls' && p.deviance !== undefined);
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {}} // 実行中は閉じない
      title={isComplete ? 'GLM Analysis Complete' : 'GLM Analysis in Progress'}
      width="lg"
      closeOnOverlayClick={false}
      showCloseButton={false}
      footer={
        isComplete ? (
          <Button onClick={onClose} variant="primary">
            OK
          </Button>
        ) : onCancel ? (
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
        ) : undefined
      }
    >
      <div className={styles.dialogContent}>
          {/* 現在の進捗状況 - 常に固定高さで表示 */}
          <div className={styles.currentProgress}>
            {progress && (
              <>
                {progress.stage === 'theta-estimation' && (
                  <div>
                    <div className={styles.statusText}>Estimating dispersion parameter (θ)...</div>
                    <div className={styles.iterationInfo}>
                      Iteration {progress.iteration} / {progress.maxIterations}
                    </div>
                    {progress.details && <div className={styles.details}>{progress.details}</div>}
                  </div>
                )}
                {progress.stage === 'irls' && progress.isDuringThetaEstimation && (
                  <div>
                    <div className={styles.statusText}>
                      Evaluating θ = {progress.theta?.toFixed(4)} (iteration {progress.thetaIteration})
                    </div>
                    <div className={styles.iterationInfo}>
                      IRLS iteration {progress.iteration} / {progress.maxIterations}
                    </div>
                  </div>
                )}
                {progress.stage === 'irls' && !progress.isDuringThetaEstimation && (
                  <div>
                    <div className={styles.statusText}>Final IRLS fitting...</div>
                    <div className={styles.iterationInfo}>
                      Iteration {progress.iteration} / {progress.maxIterations}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Convergence History - 常に表示（レイアウトシフト防止） */}
          <div className={styles.historySection}>
            <div className={styles.historyHeader}>
              <h3>Convergence History</h3>
              <div style={{ display: 'flex', gap: '8px' }}>
                {hasThetaEstimation && (
                  <button
                    onClick={() => setShowAllIterations(!showAllIterations)}
                    className={styles.toggleButton}
                  >
                    {showAllIterations ? 'Show Final Only' : 'Show All Iterations'}
                  </button>
                )}
                {onSaveAsDataset && displayEntries.length > 0 && (
                  <button
                    onClick={() => {
                      onSaveAsDataset();
                      setDatasetSaved(true);
                    }}
                    className={styles.toggleButton}
                    title={!isComplete ? 'Available after analysis completes' : 'Save convergence history as a dataset'}
                    disabled={!isComplete || datasetSaved}
                  >
                    {datasetSaved ? '✓ Saved as Dataset' : 'Save as Dataset'}
                  </button>
                )}
              </div>
            </div>

            <div className={styles.tableContainer}>
              <table className={styles.historyTable}>
                <thead>
                  <tr className={styles.historyTableHeader}>
                    {hasThetaEstimation && <th className={styles.historyTableCell}>θ-iter</th>}
                    {hasThetaEstimation && (
                      <th className={styles.historyTableCell} title="Evaluation point in golden section search. Mid1 and Mid2 represent the two interior points used in each iteration.">
                        GS
                      </th>
                    )}
                    {hasThetaEstimation && <th className={`${styles.historyTableCell} ${styles.rightAlign}`}>θ</th>}
                    <th className={styles.historyTableCell}>IRLS-iter</th>
                    <th className={`${styles.historyTableCell} ${styles.rightAlign}`}>Deviance</th>
                    {hasThetaEstimation && <th className={`${styles.historyTableCell} ${styles.rightAlign}`}>Neg Log-Lik</th>}
                    {variableNames && variableNames.map((name, i) => (
                      <th key={i} className={`${styles.historyTableCell} ${styles.rightAlign}`}>{name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayEntries.map((p, idx) => (
                    <tr key={idx} className={styles.historyTableRow}>
                      {hasThetaEstimation && <td className={styles.historyTableCell}>{p.thetaIteration !== undefined ? p.thetaIteration : finalThetaIterationNumber}</td>}
                      {hasThetaEstimation && <td className={styles.historyTableCell}>{p.goldenSectionIndicator || '-'}</td>}
                      {hasThetaEstimation && <td className={`${styles.historyTableCell} ${styles.rightAlign}`}>{p.theta?.toFixed(4) || '-'}</td>}
                      <td className={styles.historyTableCell}>{p.iteration}</td>
                      <td className={`${styles.historyTableCell} ${styles.rightAlign}`}>{p.deviance?.toFixed(6) || '-'}</td>
                      {hasThetaEstimation && <td className={`${styles.historyTableCell} ${styles.rightAlign}`}>{p.logLikelihood?.toFixed(2) || '-'}</td>}
                      {variableNames && variableNames.map((_, i) => (
                        <td key={i} className={`${styles.historyTableCell} ${styles.rightAlign}`}>
                          {p.coefficients && p.coefficients[i] !== undefined ? p.coefficients[i].toFixed(4) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
      </div>
    </Dialog>
  );
}
